import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, mergeMap, Observable, of } from "rxjs";
import { CategoryEnum, PurposeEnum } from "src/app/models/shared-models";
import { SessionService } from "src/app/services/session.service";
import { environment } from "src/environments/environment";
import { ICategoryPurposeDataModel, INotificationAreaLimitModel, INotificationsForLotPlanModel, IPurposeModel } from "../models/veg-wizard-models";
import { Logger } from "./logger-service";

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  constructor(protected http: HttpClient,
    private logger: Logger,
    private sessionService: SessionService) {
  }

  setCategoriesForPurpose$(categoryPurposes: ICategoryPurposeDataModel[], sessionId: string | undefined): Observable<boolean> {

    const categories: { categoryCode: string, categoryPurposeId: number, categoryDescription: string, isProposed: boolean }[] = [];
    for (const category of categoryPurposes) {
      categories.push({ categoryCode: category.categoryCode, categoryPurposeId: category.categoryPurposeId, categoryDescription: category.purposeDescription, isProposed: category.isProposed });
    }

    return this.http.post<boolean>(
      `${environment.apiUrl}/session/set-categories`, { categories: categories, sessionKey: sessionId })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.logger.error('setCategoriesForPurpose error', err);
          return of(false)
        }),
        map(() => true) // return true, as it should be HttpStatusCode OK
      );
  }

  getCategoriesForPurpose$(purpose: string): Observable<ICategoryPurposeDataModel[]> {
    const url = `${environment.apiUrl}/session/categories-for-purpose`;
    const sessionId = this.sessionService.getSessionIdOrError();

    const params = new HttpParams({
      fromObject: {
        'sessionKey': sessionId,
        'purpose': purpose,
      }
    })

    return this.http.get<ICategoryPurposeDataModel[]>(url, { params: params })

  }

  getPreviousNotifications$(lot: string, plan: string,
    purposeCode: string, categories: string[] | null, excludeExpiredLegislation: boolean = false)
    : Observable<INotificationsForLotPlanModel> {

    const url = `${environment.apiUrl}/session/notifications-for-lot-plan?excludeExpiredLegislation=${excludeExpiredLegislation}`;
    const params = new HttpParams({
      fromObject: {
        'Lot': lot,
        'PLan': plan,
        'PurposeCode': purposeCode,
        'Categories': categories ?? '',
      }
    })

    return this.http.get<INotificationsForLotPlanModel>(url, { params: params })
  }

  shouldLoadNotificationAreaLimit(purpose: IPurposeModel): boolean {

    return !!purpose && (purpose.purposeCode === PurposeEnum.agriculturalEfficiency
      || purpose.purposeCode === PurposeEnum.agriculture
      || purpose.purposeCode === PurposeEnum.extractiveIndustry);
  }


  getNotificationAreaLimitMessage(
    limit: INotificationAreaLimitModel | undefined,
    categories: ICategoryPurposeDataModel[],
    purpose: IPurposeModel): string {
    if (limit == null) {
      return '';
    }

    if (purpose == null) {
      throw new Error('No purpose found. Cannot display clearing area limit message.');
    }

    const categoryBSelected = categories.some(category => category.categoryCode == CategoryEnum.catB);
    const isExtractiveIndustryPurpose = purpose.purposeCode === PurposeEnum.extractiveIndustry;
    if (isExtractiveIndustryPurpose && !categoryBSelected) {
      return '';
    }

    const categoryDisplay = categories
      .map((category) => category.categoryCode)
      .join(' and ');

    const lineOne = (isExtractiveIndustryPurpose
      ? 'The maximum area which can be cleared in a Category B area for an Extractive industry is 5 hectares.'
      : `The maximum area that can be cleared in a Category ${categoryDisplay} area for ${purpose.purposeDescription}
              is ${this.formatHectares(limit.maxAreaLimitInHectares)}.`);

    const areaLimitReached = limit.remainingAreaLimitInHectares == 0;
    let lineTwo = '';
    if (isExtractiveIndustryPurpose) {
      lineTwo = areaLimitReached
        ? '<br><br>Due to previous notification/s for clearing for this purpose, further notifications are not permitted.'
        : ' This includes clearing done under previous notifications.';
    } else {
      lineTwo = areaLimitReached
        ? `<br><br>Due to previous notification for clearing, further clearing in a Category ${categoryDisplay} area is not permitted.`
        : `<br><br>This includes clearing done under previous notifications.`;
    }

    return `${lineOne}${lineTwo}`;
  }


  private formatHectares(hectares: number): string {
    const hectaresToSquareMetersMultiplier = 10000;

    return hectares <= 1 ?
      `${hectares} hectares (${hectares * hectaresToSquareMetersMultiplier}m<sup><small>2</small></sup>)` :
      `${hectares} hectares`;
  }

}
